import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import {
    disableApiAccess,
    enableApiAccess,
    getApiAccessCorporations,
    getApiAccessKeys,
    revokeApiKey,
} from "../../api/queries/thirdPartyApiAccessAPI";
import {
    IApiAccessCorporation,
    IApiAccessKey,
} from "../../pages/Osorno/ApiAccessOversight/ApiAccessOversightInterfaces";

/**
 * Data-access for the Third-party Integration API MBO oversight surface (OS-2551).
 * Same DI/hook shape as CorporationsEntity: each method is a custom hook wrapping a
 * useQuery/useMutation and is called at the top level of a component/hook.
 */
export const ThirdPartyApiAccessEntity = {
    GetApiAccessCorporations: () => {
        // Mount-time `now` keeps the initial query variable stable (a fresh value each render
        // would make Apollo refetch on every render); every refetch passes a fresh `now` so the
        // active-key filter (`expires_at > now`) does not go stale on a long-lived oversight page.
        const initialNow = useMemo(() => new Date().toISOString(), []);
        const [corporations, setCorporations] = useState<IApiAccessCorporation[]>([]);

        const { loading, error, refetch } = useQuery(getApiAccessCorporations, {
            fetchPolicy: "no-cache",
            variables: { now: initialNow },
            onCompleted: (data: { corporate: IApiAccessCorporation[] }) => {
                if (data?.corporate) {
                    setCorporations(data.corporate);
                }
            },
        });

        const refetchCorporations = useCallback(() => refetch({ now: new Date().toISOString() }), [refetch]);

        return { loading, corporations, apolloQueryError: error, refetch: refetchCorporations };
    },
    GetApiAccessKeys: (corporate_id: number | undefined) => {
        const [keys, setKeys] = useState<IApiAccessKey[]>([]);

        const { loading, error, refetch } = useQuery(getApiAccessKeys, {
            fetchPolicy: "no-cache",
            skip: !corporate_id,
            variables: { corporate_id },
            onCompleted: (data: { third_party_api_key: IApiAccessKey[] }) => {
                if (data?.third_party_api_key) {
                    setKeys(data.third_party_api_key);
                }
            },
        });
        return { loading, keys, apolloQueryError: error, refetch };
    },
    EnableApiAccessOsornoBO: () => {
        const [enableApiAccessMutation, { error, loading }] = useMutation(enableApiAccess);
        return { apolloMutationError: error, enableApiAccessMutation, loading };
    },
    DisableApiAccessOsornoBO: () => {
        const [disableApiAccessMutation, { error, loading }] = useMutation(disableApiAccess);
        return { apolloMutationError: error, disableApiAccessMutation, loading };
    },
    RevokeApiKeyOsornoBO: () => {
        const [revokeApiKeyMutation, { error }] = useMutation(revokeApiKey);
        return { apolloMutationError: error, revokeApiKeyMutation };
    },
};
